import { getComponentJobCompanionPaths, getComponentRunPaths } from '@secureblob/sdk/componentJobs'

export type ComponentJobRunTarget = 'local' | 'server'

export interface ComponentJobControlPaths {
    runsDir: string
    requestsDir: string
    localRunRequest: string
    serverRunRequest: string
}

export interface ComponentRunControlPaths {
    runDir: string
    cancelRequest: string
}

interface ComponentJobControlDeps {
    writeFile: (path: string, content?: string) => void
    mkdir?: (path: string) => void
}

export function getComponentJobControlPaths(sourcePath: string): ComponentJobControlPaths {
    const { runsDir } = getComponentJobCompanionPaths(sourcePath)
    const requestsDir = `${runsDir}/_requests`
    return {
        runsDir,
        requestsDir,
        localRunRequest: `${requestsDir}/local-run.json`,
        serverRunRequest: `${requestsDir}/server-run.json`,
    }
}

export function getComponentRunControlPaths(sourcePath: string, runId: string): ComponentRunControlPaths {
    const { runDir } = getComponentRunPaths(sourcePath, runId)
    return {
        runDir,
        cancelRequest: `${runDir}/cancel-request.json`,
    }
}

export function hasComponentJobRunRequest(
    fileSet: ReadonlySet<string>,
    sourcePath: string,
    target: ComponentJobRunTarget,
): boolean {
    const paths = getComponentJobControlPaths(sourcePath)
    return fileSet.has(target === 'local' ? paths.localRunRequest : paths.serverRunRequest)
}

export function hasComponentJobCancelRequest(
    fileSet: ReadonlySet<string>,
    sourcePath: string,
    runId: string,
): boolean {
    return fileSet.has(getComponentRunControlPaths(sourcePath, runId).cancelRequest)
}

export function writeComponentJobRunRequest(
    sourcePath: string,
    target: ComponentJobRunTarget,
    { writeFile, mkdir }: ComponentJobControlDeps,
): string {
    const paths = getComponentJobControlPaths(sourcePath)
    ensureDirectory(mkdir, paths.runsDir)
    ensureDirectory(mkdir, paths.requestsDir)

    const requestPath = target === 'local' ? paths.localRunRequest : paths.serverRunRequest
    writeFile(
        requestPath,
        `${JSON.stringify({
            action: 'run',
            target,
            sourcePath,
            requestedAt: new Date().toISOString(),
            requestedBy: 'webapp',
        }, null, 2)}\n`,
    )

    return requestPath
}

export function writeComponentJobCancelRequest(
    sourcePath: string,
    runId: string,
    { writeFile, mkdir }: ComponentJobControlDeps,
): string {
    const paths = getComponentRunControlPaths(sourcePath, runId)
    ensureDirectory(mkdir, paths.runDir)

    writeFile(
        paths.cancelRequest,
        `${JSON.stringify({
            action: 'cancel',
            runId,
            sourcePath,
            requestedAt: new Date().toISOString(),
            requestedBy: 'webapp',
        }, null, 2)}\n`,
    )

    return paths.cancelRequest
}

function ensureDirectory(mkdir: ((path: string) => void) | undefined, path: string) {
    if (!mkdir) return
    try {
        mkdir(path)
    } catch {
        // Directory may already exist; the request write is the meaningful operation.
    }
}
